import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import './Login.css';

// Successful login always lands on the Dashboard. The package picker is
// the canonical home — it shows users their accessible packages and lets
// them dive into one. We intentionally ignore any `?returnUrl=` from
// PrivateRoute redirects so a deep link bounce-back doesn't surprise the
// user mid-login.
const POST_LOGIN_PATH = '/';

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false); // cosmetic — no server-side "remember" yet
  const [error, setError] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // First boot: redirect to /setup so the admin gets created.
  useEffect(() => {
    let alive = true;
    api.bootstrapStatus()
      .then((data) => { if (alive && data?.available) navigate('/setup', { replace: true }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [navigate]);

  // Already-signed-in users hitting /login bounce to the dashboard.
  useEffect(() => {
    if (user) navigate(POST_LOGIN_PATH, { replace: true });
  }, [user, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(username, password);
      navigate(POST_LOGIN_PATH, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sgg-login">
      <div className="sgg-login__inner">
        <div className="sgg-login__logo">
          <span className="sgg-login__logo-stream">STREAM</span>
          <span className="sgg-login__logo-gg">GG</span>
        </div>
        <div className="sgg-login__subtitle">Tournament Overlay System</div>

        <div className="sgg-login__card">
          <h1 className="sgg-login__heading">Player Login</h1>

          {error && <div className="sgg-login__msg sgg-login__msg--error">{error}</div>}
          {showReset && (
            <div className="sgg-login__msg sgg-login__msg--info">
              Passwords are managed by your admin — ask them to reset yours.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="sgg-login__field">
              <input
                type="text"
                className="sgg-login__input"
                placeholder="USERNAME"
                aria-label="Username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="sgg-login__field">
              <input
                type="password"
                className="sgg-login__input"
                placeholder="PASSWORD"
                aria-label="Password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="sgg-login__row">
              <label className="sgg-login__remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember me
              </label>
              <button
                type="button"
                className="sgg-login__forgot"
                onClick={() => setShowReset(true)}
              >
                Forgot?
              </button>
            </div>

            <button type="submit" className="sgg-login__btn" disabled={submitting}>
              {submitting ? 'SIGNING IN…' : 'INSERT CREDENTIALS'}
            </button>
          </form>
        </div>

        <div className="sgg-login__footer">
          © 2026 STREAMGG · Data from{' '}
          <a href="https://start.gg" target="_blank" rel="noreferrer">start.gg</a>
        </div>
      </div>
    </div>
  );
}
